import { createContext, useContext, useEffect, useMemo } from "react";
import axios, {
  Axios,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from "axios";
import axiosRetry from 'axios-retry';

import { useSnackbar } from "@shared/hooks";
import { LocalStorageKey } from "@shared/entities";
import { useConfigService } from "./ConfigService";
import { useAuthService } from "./AuthService";
import { isDev } from "@shared/Utils";
import { useTranslation } from "react-i18next";
import AppConst from "@shared/entities/AppConsts";

/** 不顯示錯誤訊息的API路徑（例如報名資訊不在時的404）*/
const ignoreErrorMessagePath = [
  `/registration/${AppConst.eventID}`,
  `/registration/${AppConst.eventID}/dealersDen`,
  `/afterParty/${AppConst.eventID}`,
  `/hotelReservation/reservation/${AppConst.eventID}`,
  `fursuiter/${AppConst.eventID}/changeRoom`,
  `/boats/${AppConst.eventID}`,
];

const Context = createContext<Axios>(null);

/** 是否不顯示錯誤訊息的Path */
function isErrorIgnored(config: AxiosRequestConfig) {
  const path = config.url;

  if (config.method.toLocaleUpperCase() !== "GET") {
    return;
  }
  for (let p of ignoreErrorMessagePath) {
    if (path.includes(p)) {
      return true;
    }
  }
  return false;
}

/** 是否被CloudFlare給阻擋 */
function isChallengedByCloudFlare(error: AxiosError) {
  const headers = error?.response?.headers
  if (!headers) {
    return;
  }
  if (isDev()) {
    console.log(headers);
  }
  if (headers['cf-mitigated'] === 'challenge') {
    return true;
  }
}
export function useHttpService() {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useHttpService 必須在HttpService之下層執行！");
  }
  return context;
}

/**
 * 解釋用法：
 * 原始的axios用來處理不需要登入的API.
 * useHttpService() 用來處理需要登入的API.
 * */
export const HttpService = ({ children }) => {
  const [openSnackbar] = useSnackbar();
  const { frontendConfig } = useConfigService();
  const { RefreshJWTToken } = useAuthService();

  axios.defaults.baseURL = frontendConfig?.baseURL;

  const httpClient = useMemo(() => axios.create(), []);

  const [t] = useTranslation();

  useEffect(() => {
    axios.interceptors.request.clear();
    axios.interceptors.response.clear();

    /** 回應處理器 */
    axios.interceptors.response.use(
      (resp: AxiosResponse) => {
        return resp;
      },
      async (error: AxiosError) => {
        const data = error.response?.data;
        if (!isErrorIgnored(error.config)) {
          /** 如果是我們的錯誤訊息Response, 試著取裡面的訊息 */
          if (!(data instanceof String) && data) {
            const message = (data as any)?.message;
            const code = t((data as any)?.code);
            const ns = code?.split(".")?.[0];
            const key = code?.substring(code?.indexOf(".") + 1);
            if (message || code) {
              openSnackbar(t(key, { ns, defaultValue: message }), "negative");
            } else {
              openSnackbar(t('errors.unexcepted', { ns: 'common' }), "negative");
            }
          } else {
            openSnackbar(t('errors.unexcepted', { ns: 'common' }), "negative");
          }
        }
        throw error;
      }
    );

    httpClient.interceptors.request.clear();
    httpClient.interceptors.response.clear();

    httpClient.defaults = axios.defaults;

    axiosRetry(httpClient, {
      retries: 3, // number of retries
      retryDelay: (retryCount) => {
        console.warn(`連線重試...: ${retryCount}`);
        return retryCount * 2000; // time interval between retries
      },
      retryCondition: (error) => {
        // 520為Cloudflare proxy說伺服器端「回傳的東西無法處理」或「沒有回傳資料」
        // 應用於售票亭被推倒時的重試情況
        return error?.response?.status === 520;
      },
    });

    /** 要求處理器  */
    httpClient.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem(LocalStorageKey.AccessToken);
        if (token) {
          config.headers.set("Authorization", `Bearer ${token}`);
        }
        return config;
      },
      (error: any) => {
        console.error(error);

        throw error;
      }
    );

    /** 回應處理器 */
    httpClient.interceptors.response.use(
      (resp: AxiosResponse) => {
        return resp;
      },
      async (error: AxiosError) => {
        if (isDev()) {
          console.error(error);
        }
        const data = error.response?.data;

        if (isChallengedByCloudFlare(error)) {
        }
        // 如果狀態是 401時, 有可能是JWT Token過期, 進行JWTToken的重取
        if (
          error.response?.status === 401 &&
          (data as any)?.code === "common.errors.unauthorized"
        ) {
          const newToken = await RefreshJWTToken();
          // 取得新Token成功的狀態下
          if (newToken?.success) {
            // 再重打一次API！
            error.config.headers.Authorization = `Bearer ${newToken.jwt}`;
            return httpClient.request(error.config);
          }

        }
        /** 如果是我們的錯誤訊息Response, 試著取裡面的訊息 */
        if (!isErrorIgnored(error.config)) {
          if (!(data instanceof String) && data) {
            const message = (data as any)?.message;
            const code = t((data as any)?.code);
            const ns = code?.split(".")?.[0];
            const key = code?.substring(code?.indexOf(".") + 1);
            if (message || code) {
              openSnackbar(t(key, { ns, defaultValue: message }), "negative");
            } else {
              openSnackbar(t('errors.unexcepted', { ns: 'common' }), "negative");
            }
          } else {
            openSnackbar(t('errors.unexcepted', { ns: 'common' }), "negative");
          }
        }

        throw error;
      }
    );
  }, [RefreshJWTToken, frontendConfig.baseURL, httpClient, openSnackbar, t]);

  return <Context.Provider value={httpClient}>{children}</Context.Provider>;
};
